/**
 * Bin selection and ordering (spec §9.7 `bin`, §9.19 energy matching) — mirror of `bins.py`.
 * Open, live tasks matching the filter; no notes, none set aside or not yet up, no `someday`/blocked/queued unless the
 * config says so; energy ≤ the device's "my energy now". Default sort: want desc, energy asc, createdAt asc.
 */
import { isNotYet } from "./notYet";
import { computeAll, type ComputedKey } from "./computed";
import { dayIn, evaluate, matches, type FilterAst, type FilterCtx } from "./filter";
import { fit, type FitReason, type SignalAst, type SignalRule, type Signals } from "./signals";
import { Snapshot, groupsOf, sequencesOf } from "./structure";
import type { Task } from "./schema";

export const DEFAULT_ENERGY = 3;

export interface BinSort {
  by: "want" | "energy" | "createdAt" | "due" | "title" | ComputedKey | string;
  dir: "asc" | "desc";
}

export interface BinConfig {
  filter?: FilterAst;
  sort?: BinSort[];
  limit?: number;
  includeBlocked?: boolean;
  showQueued?: boolean;
  includeSomeday?: boolean;
  energyMax?: number | null;
  /** tasks matching this rise, others step back (ADR 0016) */
  prefer?: FilterAst | null;
}

export const DEFAULT_SORT: BinSort[] = [
  { by: "want", dir: "desc" },
  { by: "energy", dir: "asc" },
  { by: "createdAt", dir: "asc" },
];

export interface BinRow {
  task: Task;
  computed: Record<ComputedKey, unknown>;
  /** signal fit (ADR 0016): +1 per condition that holds, -1 per one that does not */
  fit: number;
  reasons: FitReason[];
}

export function energyOf(task: Task): number {
  const e = task.props.energy;
  return typeof e === "number" ? e : DEFAULT_ENERGY;
}

function sortKey(row: BinRow, by: string): number | string {
  const { task, computed: c } = row;
  if (by === "want") return c.want as number;
  if (by === "energy") return energyOf(task);
  if (by === "createdAt") return task.createdAt;
  if (by === "due") return typeof task.props.due === "string" ? task.props.due : "9999";
  if (by === "title") return task.title.toLowerCase();
  if (by in c) {
    const v = c[by as ComputedKey];
    return typeof v === "number" ? v : v ? 1 : 0;
  }
  const v = task.props[by];
  return typeof v === "number" ? v : String(v ?? "");
}

function cmp(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

export function selectBin(
  s: Snapshot,
  config: BinConfig,
  nowMs: number,
  energyMax?: number | null,
  signals?: Signals | null,
  rules?: SignalRule[] | null,
): BinRow[] {
  const emax = config.energyMax ?? energyMax ?? null;
  const ctx: FilterCtx = {
    signals: signals ?? null,
    computed: (t: Task, key: string) => (computeAll(s, t.id, nowMs) as Record<string, unknown>)[key],
    groupsOf: (id: string) => groupsOf(s, id),
    seqsOf: (id: string) => sequencesOf(s, id),
    parentOf: (t: Task) => s.parentOf(t.id),
    today: dayIn(nowMs),
  };
  const rows: BinRow[] = [];
  for (const [id, rec] of Object.entries(s.tasks)) {
    const task = {
      ...(rec as unknown as Task),
      id,
      props: ((rec as { props?: Task["props"] }).props ?? {}) as Task["props"],
    };
    if (task.archivedAt || task.doneAt || task.typeId === "note") continue;
    if (isNotYet(task, nowMs)) continue; // not before a later time (or set aside), or inside its minimum gap
    if (!config.includeSomeday && task.props.horizon === "someday") continue;
    const computed = computeAll(s, id, nowMs);
    if (!config.includeBlocked && computed.blocked) continue;
    if (!config.showQueued && !computed.active) continue;
    if (emax !== null && energyOf(task) > emax) continue;
    if (!matches(task, config.filter, ctx)) continue;
    const f = fit(
      task,
      (t, a) => evaluate(t, a as FilterAst, ctx),
      rules,
      (config.prefer as SignalAst) ?? null,
    );
    if (f.hidden) continue;
    rows.push({ task, computed, fit: f.score, reasons: f.reasons });
  }
  const sort = config.sort?.length ? config.sort : DEFAULT_SORT;
  rows.sort((a, b) => {
    if (a.fit !== b.fit) return b.fit - a.fit; // fit first, then the bin's own order
    for (const { by, dir } of sort) {
      const r = cmp(sortKey(a, by), sortKey(b, by));
      if (r !== 0) return dir === "desc" ? -r : r;
    }
    return 0;
  });
  return config.limit && config.limit > 0 ? rows.slice(0, config.limit) : rows;
}
